import { Droplets, Receipt, TrendingUp, Wallet } from 'lucide-react';

import { DualAxisChart } from '@/components/charts/dual-axis-chart';
import type { DailySale } from '../dashboard.api';
import { formatCompact, formatCurrency, toNumber } from '@/lib/utils';

interface DailySalesChartProps {
  data: DailySale[] | undefined;
  loading: boolean;
  height?: number;
}

const SKELETON_HEIGHTS = [42, 68, 30, 82, 55, 92, 47, 72, 36, 62, 88, 52];

/** Altura del área de barras en píxeles (las alturas % requieren un padre definido). */
const CHART_HEIGHT = 176;

/** Espacio reservado bajo las barras para la etiqueta del eje. */
const LABEL_SPACE = 20;

/**
 * Gráfica de barras de ventas por día.
 *
 * Se implementa con CSS (sin librerías de charts) porque el volumen de datos es
 * bajo (una barra por día) y así se evita sumar una dependencia pesada al bundle.
 */
export function DailySalesChart({ data, loading, height }: DailySalesChartProps) {
  if (loading) {
    return (
      <div
        className="flex items-end gap-1.5 sm:gap-2"
        style={{ height: CHART_HEIGHT + LABEL_SPACE }}
      >
        {SKELETON_HEIGHTS.map((height, index) => (
          <div
            key={index}
            style={{ height: Math.round((CHART_HEIGHT * height) / 100) }}
            className="flex-1 animate-pulse rounded-t-md bg-ink-100"
          />
        ))}
      </div>
    );
  }

  const series = (data ?? []).filter((item) => item && item.fecha);

  if (series.length === 0) {
    return (
      <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-ink-200 text-center">
        <TrendingUp className="size-6 text-ink-300" aria-hidden="true" />
        <p className="text-sm text-ink-500">Sin ventas registradas en este rango.</p>
      </div>
    );
  }

  const totalAmount = series.reduce((sum, item) => sum + toNumber(item.monto), 0);
  const totalGallons = series.reduce((sum, item) => sum + toNumber(item.galones), 0);
  const totalTransactions = series.reduce((sum, item) => sum + toNumber(item.transacciones), 0);

  return (
    <div className="flex h-full flex-col justify-center">
      <DualAxisChart
        labels={series.map((item) => item.fecha)}
        series={[
          {
            label: 'Ventas (S/)',
            data: series.map((item) => toNumber(item.monto)),
            axis: 'left',
            kind: 'line',
            color: '#06b6d4',
          },
          {
            label: 'Galones',
            data: series.map((item) => toNumber(item.galones)),
            axis: 'right',
            kind: 'line',
            color: '#10b981',
          },
        ]}
        leftTitle="Ventas (S/)"
        rightTitle="Galones"
        leftFormat="currency"
        rightFormat="number"
        height={height}
      />

      <div className="mt-3 flex items-center justify-between border-t border-ink-100 pt-3 text-xs text-ink-500">
        <span className="flex items-center gap-1.5">
          <Wallet className="size-3.5 text-brand-500" aria-hidden="true" />
          Total del rango: <strong className="font-semibold text-ink-800">{formatCurrency(totalAmount)}</strong>
        </span>

        <span className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <Droplets className="size-3.5 text-accent-500" aria-hidden="true" />
            {formatCompact(totalGallons)} gal
          </span>
          <span className="flex items-center gap-1.5">
            <Receipt className="size-3.5 text-ink-400" aria-hidden="true" />
            {formatCompact(totalTransactions)} ops
          </span>
        </span>
      </div>
    </div>
  );
}
